import React from 'react';
import { LAYER_AVERAGES } from '../../mock';
import { useI18n } from '../../i18n';

export default function Readiness() {
  const { t, tf } = useI18n();
  const sorted = [...LAYER_AVERAGES].sort((a, b) => a.score - b.score);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];

  // Radar geometry
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const R = 120;
  const n = LAYER_AVERAGES.length;
  const angle = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pt = (i, val) => {
    const rr = (val / 100) * R;
    return [cx + rr * Math.cos(angle(i)), cy + rr * Math.sin(angle(i))];
  };
  const polygon = LAYER_AVERAGES.map((l, i) => pt(i, l.score).join(',')).join(' ');

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-display text-xl font-extrabold text-slate-900">{t('readinessTitle')}</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl leading-relaxed">{t('readinessSub')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center justify-center">
          <svg width={size} height={size}>
            {[0.25, 0.5, 0.75, 1].map((f, i) => (
              <circle key={i} cx={cx} cy={cy} r={R * f} fill="none" stroke="#F1F5F9" />
            ))}
            {LAYER_AVERAGES.map((l, i) => {
              const [ex, ey] = pt(i, 100);
              return <line key={i} x1={cx} y1={cy} x2={ex} y2={ey} stroke="#F1F5F9" />;
            })}
            <polygon points={polygon} fill="rgba(99,102,241,0.18)" stroke="#6366F1" strokeWidth="2" />
            {LAYER_AVERAGES.map((l, i) => {
              const [px, py] = pt(i, l.score);
              return <circle key={i} cx={px} cy={py} r={4} fill={l.color} stroke="#fff" strokeWidth="1.5" />;
            })}
            {LAYER_AVERAGES.map((l, i) => {
              const [lx, ly] = pt(i, 118);
              return (
                <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                  fontSize="10" className="fill-slate-500">{l.order}</text>
              );
            })}
          </svg>
        </div>

        {/* Summary */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs text-slate-400">{t('weakestLayer')}</div>
              <div className="font-display font-bold text-slate-900 mt-1 text-sm">{tf(weakest)}</div>
              <div className="font-display text-2xl font-extrabold mt-2" style={{ color: weakest.color }}>{weakest.score}%</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs text-slate-400">{t('strongestLayer')}</div>
              <div className="font-display font-bold text-slate-900 mt-1 text-sm">{tf(strongest)}</div>
              <div className="font-display text-2xl font-extrabold mt-2" style={{ color: strongest.color }}>{strongest.score}%</div>
            </div>
          </div>
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-2xl p-5">
            <p className="text-sm text-indigo-600 leading-relaxed">{t('readinessSub')}</p>
          </div>
        </div>
      </div>

      {/* Layer cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {LAYER_AVERAGES.map((l) => (
          <div key={l.key} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                style={{ background: l.color }}>{l.order}</span>
              <span className="font-display text-xl font-extrabold" style={{ color: l.color }}>{l.score}%</span>
            </div>
            <h4 className="font-display font-bold text-slate-900 text-sm mt-3 leading-snug">{tf(l)}</h4>
            <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${l.score}%`, background: l.color }} />
            </div>
            <p className="text-xs text-slate-400 mt-2">{t('layerScore')}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
